import { getSubdomainData } from '@apparel/apparel/src/lib/utils';
import { APIService } from '@apparel/core/api';
import { fields, mergeConfig } from '@apparel/core/api/utils';
import { AuthService, AUTH_EVENT } from '@apparel/core/auth';
import { Log, LogChannel } from '@apparel/core/log';
import { localStorage } from '@apparel/core/storage';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { v4 as uuid } from 'uuid';
import { CartEmitter, CartEvents } from './CartEmitter';
import { CartItem } from './CartItem';
import { CartItemPackage, CartPrices, CartResult } from './CartResult';
import { PackageList } from './PackageList';
import { SkuList } from './SkuList';
import { itemQuantity } from './utils';

const CART_ITEMS_KEY = 'CART_SERVICE@ITEMS';

/**
 * Cart service
 */
export class CartService {
    private static __SELF__: CartService;
    private __items: CartItem[] = [];
    private auth: AuthService;
    private api: APIService;
    private endpoint: AxiosInstance;
    private logger: LogChannel;

    constructor() {
        this.auth = AuthService.getInstance();
        this.api = APIService.getInstance();
        this.endpoint = this.api.createEndpoint('cart');
        this.logger = Log.channel('CartService');

        this.init();
    }

    /**
     * Get singletone service instance
     */
    public static getInstance(): CartService {
        if (!CartService.__SELF__) {
            CartService.__SELF__ = new CartService();
        }

        return CartService.__SELF__;
    }

    /**
     * Initialize cart
     * @example
     * Initialize service outside the class: `CartService.getInstance().init();`
     */
    public init() {
        this.logger.debug('Initialize');
        this.unserialize();
        CartEmitter.emit(CartEvents.INITIALIZE);
        AuthService.getInstance().on(AUTH_EVENT.LOGIN, async (user: any) => {
            this.logger.debug('Getting user event', { user });

            if (user) {
                this.unserialize();
                if (this.__items.length > 0) {
                    await this.endpoint.post('/clear');
                    await this.uploadLocalItems();
                    this.__items = [];
                    this.serialize();
                }
                CartEmitter.emit(CartEvents.ITEMS_UPLOADED);
            }
        });
    }

    /**
     * Add item to the cart
     * @param item
     * @param merge
     */
    public async addItem(item: CartItem, merge = true): Promise<CartItem> {
        item.quantity = itemQuantity(item.quantity);
        if (this.isAuth()) {
            const { data } = await this.endpoint.post('/items', { ...item, merge });
            CartEmitter.emit(CartEvents.ADD, data);
            return data;
        }

        let found = false;

        if (merge) {
            this.__items = this.__items.map((cartItem) => {
                if (
                    cartItem.sku_id === item.sku_id &&
                    cartItem.package_id === item.package_id &&
                    cartItem.factory_id === item.factory_id &&
                    cartItem.image_id === item.image_id
                ) {
                    found = true;
                    return {
                        ...cartItem,
                        quantity: itemQuantity(itemQuantity(cartItem.quantity) + itemQuantity(item.quantity)),
                    };
                }

                return cartItem;
            });
        }

        let newItem: any;
        if (!found) {
            const maxId = Math.max(0, ...this.__items.map(({ id }) => id || 0));

            newItem = {
                ...item,
                merge: false,
                id: item.id ?? maxId + 1,
            };

            this.__items = [...this.__items, newItem];
        }

        this.serialize();

        CartEmitter.emit(CartEvents.ADD, newItem);
        return newItem;
    }

    /**
     * Duplicate item (id it's used to duplicate the item)
     * @param item
     */
    public async duplicateItem(item: CartItem): Promise<CartItem | CartItem[] | null> {
        if (!item.id) {
            return null;
        }

        if (this.isAuth()) {
            const { data } = await this.endpoint.post(`/items/${item.id}/duplicate`);
            CartEmitter.emit(CartEvents.ADD, data);
            return data;
        }

        const itemById = this.__items.find(({ id }) => id && id === item.id);
        if (!itemById) {
            return null;
        }

        let result: any;

        let maxId = Math.max(0, ...this.__items.map(({ id }) => id || 0));
        if (itemById.package_id && itemById.package_uuid) {
            const items = this.__items.filter(
                ({ package_id, package_uuid }) =>
                    package_uuid === itemById.package_uuid && package_id === itemById.package_id,
            );

            const newUUID = uuid();
            result = items.map((item) => {
                maxId++;
                return {
                    ...item,
                    id: maxId,
                    package_uuid: newUUID,
                    image_id: null,
                };
            });

            this.__items = [...this.__items, ...result];
        } else {
            result = {
                ...itemById,
                id: maxId + 1,
                image_id: null,
            };

            this.__items = [...this.__items, result];
        }

        this.serialize();
        CartEmitter.emit(CartEvents.ADD, item);

        return result;
    }

    /**
     *
     * @param item
     */
    public async removeItem(item: Partial<CartItem>) {
        if (this.isAuth()) {
            const { data } = await this.endpoint.delete(`/items/${item.id}`);
            CartEmitter.emit(CartEvents.REMOVE, data);
            return data;
        }

        const itemById = this.__items.find(({ id }) => id === item.id);

        this.__items = this.__items.filter((_item) => {
            if (!itemById) {
                return true;
            }

            if (itemById.id) {
                if (itemById.package_id && itemById.package_uuid) {
                    return itemById.package_id !== _item.package_id || itemById.package_uuid !== _item.package_uuid;
                }

                return itemById.id !== _item.id;
            }

            return (
                (itemById.sku_id && itemById.sku_id !== _item.sku_id) ||
                (itemById.package_id && itemById.package_id !== _item.package_id) ||
                (itemById.image_id && itemById.image_id !== _item.image_id)
            );
        });
        this.serialize();

        CartEmitter.emit(CartEvents.REMOVE, itemById);
        return this;
    }

    /**
     *
     * @param item
     */
    public async updateItem(item: Partial<CartItem>) {
        if (this.isAuth()) {
            const { data } = await this.endpoint.put(`/items/${item.id}`, {
                ...item,
                addons: item?.addons?.length === 0 ? 'unset' : item?.addons,
            });
            CartEmitter.emit(CartEvents.UPDATE, data);
            return data;
        }

        const itemById = this.__items.find(({ id }) => id === item.id);

        this.__items = this.__items.map((_item) => {
            if (
                item === _item ||
                (item.id && item.id === _item.id) ||
                (itemById &&
                    itemById.package_id &&
                    itemById.package_id === _item.package_id &&
                    itemById.package_uuid &&
                    itemById.package_uuid === _item.package_uuid)
            ) {
                return {
                    ..._item,
                    ...item,
                    sku_id:
                        (itemById?.id && itemById?.id === _item.id ? item.sku_id ?? itemById.sku_id : null) ??
                        _item.sku_id,
                    id: _item.id,
                };
            }

            return _item;
        });

        this.serialize();

        CartEmitter.emit(CartEvents.UPDATE, item, this.__items);
        return this;
    }

    public getItems() {
        return this.__items;
    }

    public async loadCart(config: AxiosRequestConfig = {}): Promise<CartResult> {
        const subdomainData = getSubdomainData();

        config.params = config.params ?? {};
        if (!config.params?.fields) {
            config.params.fields = [
                'id',
                [
                    'items',
                    [
                        'id',
                        'thumbnail',
                        'quantity',
                        'sku_id',
                        'factory_id',
                        'model_id',
                        'model_type',
                        'package_uuid',
                        'image_id',
                        ['factory', ['id', 'name']],
                        [
                            'sku',
                            [
                                'id',
                                'name',
                                'slug',
                                ['preview_photo', ['thumbnail_path']],
                                ['category', ['id', 'name', 'slug']],
                                ['subcategory', ['id', 'name', 'slug']],
                            ],
                        ],
                        ['package', ['id', 'name', ['subcategories', ['id']]]],
                        ['price', ['type', 'type_id', 'price', 'price_single']],
                    ],
                ],
                ['prices', ['total']],
            ];
        }

        if (this.isAuth()) {
            const { data } = await this.endpoint.get('', config);
            return data;
        }
        console.log(subdomainData);

        if (this.getItems()?.length > 0) {
            const { data } = await this.endpoint.post(
                '',
                {
                    items: this.getItems(),
                    wholesaler_id: subdomainData?.id,
                },
                config,
            );
            return data;
        }

        return {
            closed: false,
            id: 0,
            last_viewed_at: new Date(),
            type: 0,
            user_id: 0,
            items: [],
            prices: {
                post_order: false,
                subtotal: 0,
                tax: 0,
                total: 0,
                total_factory_production: 0,
                cogs: 0,
                total_cogs: 0,
                shipping_cost: 0,
                salesman_cost: 0,
                salesmen_deduction: 0,
                factory_deduction: 0,
                profit: 0,
                salesmen_commission: 0,
                shipping_method_extra: 0,
                shipping_method_adds: 0,
                bulk_discount: 0,
                discount: 0,
                addon_cost: 0,
                items: [],
            },
        };
    }

    public async countItems(): Promise<number> {
        if (this.isAuth()) {
            const { items } = await this.loadCart({
                params: {
                    fields: [
                        [
                            'items',
                            [
                                'id',
                                'sku_id',
                                'model_id',
                                'model_type',
                                'package_uuid',
                                'image_id',
                                ['package', ['id', ['subcategories', ['id']]]],
                            ],
                        ],
                    ],
                },
            });

            const skus = SkuList.fromItems(items);
            const packages = PackageList.fromItems(items);

            return skus.count() + packages.count();
        }

        const items = this.getItems();
        const skus = SkuList.fromItems(items as any);
        const packages = PackageList.fromItems(items as any);

        return skus.count() + packages.count();
    }

    public async order<T = any>(data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        config = {
            ...config,
            params: {
                ...(config?.params ?? {}),
                fields: config?.params?.fields ?? ['id', 'name', 'salesman', 'customer', 'total'],
            },
        };
        const result = await this.endpoint.post('/order', data, config);
        CartEmitter.emit(CartEvents.CLEAR);
        return result;
    }

    public async instantQuote<T = any>(data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        config = {
            ...config,
            params: {
                ...(config?.params ?? {}),
                fields: config?.params?.fields ?? ['id', 'has_only_sizing_kits'],
            },
        };

        const result = await this.endpoint.post('/instant-quote', data, config);
        CartEmitter.emit(CartEvents.CLEAR);
        return result;
    }

    public async clear() {
        if (this.isAuth()) {
            try {
                await this.endpoint.post('/clear');
            } catch (e) {
                // Do we need to treat error when cart clear fail?
            }

            CartEmitter.emit(CartEvents.CLEAR);

            return;
        }

        this.__items = [];
        this.serialize();
        CartEmitter.emit(CartEvents.CLEAR);
    }

    public async addPackage(
        item: {
            quantity?: number;
            image_id?: number;
            factory_id?: number;
            model_type?: string;
            addons?: number[];
            category_id?: number;
        } & Partial<CartItemPackage>,
    ) {
        if (this.isAuth()) {
            const { data } = await this.endpoint.post('/items/add-package', {
                category_id: item.category_id,
                quantity: item.quantity,
                package_id: item.id,
                model_id: item.id,
                sku_ids:
                    item.subcategories
                        ?.map(({ default_sku_id, id, subcategory_to_repeat, subcategory_id }) => {
                            const items = [];
                            for (let i = 0; i < subcategory_to_repeat; i++) {
                                items.push({
                                    subcategory_id: subcategory_id ?? id,
                                    sku_id: default_sku_id,
                                });
                            }
                            return items;
                        })
                        .reduce((acc, val) => acc.concat(val), []) ?? [],
                model_type: item.model_type,
                addons: item.addons,
            });

            CartEmitter.emit(CartEvents.ADD, data);
            return data;
        }

        let maxId = Math.max(0, ...this.__items.map(({ id }) => id || 0));
        const package_uuid = uuid();
        const items =
            item.subcategories?.map(({ id, default_sku_id, subcategory_to_repeat, subcategory_id }) => {
                const items = [];
                for (let i = 0; i < subcategory_to_repeat; i++) {
                    maxId++;
                    items.push({
                        id: maxId,
                        category_id: item.category_id,
                        subcategory_id: subcategory_id ?? id,
                        sku_id: default_sku_id,
                        image_id: item.image_id,
                        factory_id: item.factory_id,
                        model_id: item.id,
                        model_type: item.model_type,
                        quantity: itemQuantity(item.quantity),
                        addons: item.addons,
                        merge: false,
                        package_uuid,
                        package_id: item.id,
                    });
                }
                return items;
            }) ?? [];
        this.__items = [...this.__items, ...items.reduce((acc, val) => acc.concat(val), [])];

        this.serialize();
        CartEmitter.emit(CartEvents.ADD, items);

        return items;
    }

    private unserialize() {
        this.__items = localStorage.getItem(CART_ITEMS_KEY) || [];
    }

    private serialize() {
        localStorage.setItem(CART_ITEMS_KEY, this.__items);
    }

    private isAuth() {
        return this.auth.isAuthenticated();
    }

    private async uploadLocalItems() {
        this.unserialize();
        if (!this.__items || this.__items.length === 0) {
            return;
        }

        try {
            await this.endpoint.post('/items/upload', { items: this.__items });
            CartEmitter.emit(CartEvents.UPDATE);
        } catch (e) {
            // Nothing
        }
    }

    public emitOpenCart() {
        CartEmitter.emit(CartEvents.DROPDOWN_SHOW, true);
    }

    public emitAddOnUpdated(addOn) {
        CartEmitter.emit(CartEvents.ADDONS_UPDATED, addOn);
    }

    public static async configure() {
        await this.getInstance().configure();
    }

    private async configure() {
        this.logger.debug('Apply configuration');
    }

    public async calculatePrice(config: null | AxiosRequestConfig, ...items: CartItem[]): Promise<CartPrices> {
        const fields$ = fields([
            [
                'prices',
                ['bulk_discount', 'addon_cost', 'shipping_cost', 'subtotal', 'total', ['items', ['price_single']]],
            ],
        ]);

        const { data } = await this.endpoint.post(
            '',
            { items },
            mergeConfig(fields$, { params: { preview: true } }, config),
        );

        return data.prices;
    }

    public async updateAddonFeed(item: { id: number; feed_id: number; addon_id: number; value?: any }) {
        if (this.isAuth()) {
            if (this.isAuth()) {
                const { data } = await this.endpoint.post(`/items/${item.id}/update-addon-feed`, {
                    ...item,
                });
                CartEmitter.emit(CartEvents.UPDATE, data);
                return data;
            }
        }
    }
}
